import {Api, UpdateEventDetailsProps} from '../../api';
import {filterEventsForUser, getAuthUserCompanyId} from '../../utils';
import {createAppAsyncThunk} from '../appAsyncThunk';
import {CompanySelector} from '../company/CompanySelector';
import {UserSelector} from '../user/UserSelector';
import {EventSelector} from './EventSelector';

// Fetch future events thunk (for initial load)
export const fetchFutureEvents = createAppAsyncThunk(
  'events/fetchFutureEvents',
  async (params: {isAdmin?: boolean} = {}, {getState}) => {
    try {
      const userCompanyId = await getAuthUserCompanyId();

      // Get future events
      const {events: futureEvents} =
        await Api.event.getFutureEvents(userCompanyId);
      let allEvents = futureEvents;

      // For employees, also fetch events with active evaluations
      if (!params.isAdmin) {
        const state = getState();
        const user = UserSelector.getUserDetails(state);

        if (user) {
          try {
            const {events: evaluationEvents} =
              await Api.event.getEventsForEvaluation(userCompanyId, user.id);
            // Combine future events with evaluation events, removing duplicates
            const eventIds = new Set(futureEvents.map(e => e.id));
            const uniqueEvaluationEvents = evaluationEvents.filter(
              e => !eventIds.has(e.id),
            );

            allEvents = [...futureEvents, ...uniqueEvaluationEvents].sort(
              (a, b) => {
                const dateA = a.startDateTime.toDate();
                const dateB = b.startDateTime.toDate();
                return dateA.getTime() - dateB.getTime();
              },
            );
          } catch (evaluationError) {
            console.warn('Could not fetch evaluation events:', evaluationError);
            // Continue with just future events if evaluation fetch fails
          }
        }
      }

      // Filter events for employees (admins see all events)
      let filteredEvents = allEvents;
      if (!params.isAdmin) {
        const state = getState();
        const user = UserSelector.getUserDetails(state);
        const company = CompanySelector.getCompanyDetails(state);

        if (user) {
          filteredEvents = filterEventsForUser(allEvents, user, company);
        }
      }

      // Return payload with filtered events
      return {
        events: filteredEvents,
      };
    } catch (error) {
      console.error('Error fetching future events:', error);
      throw new Error('Error fetching future events');
    }
  },
  {
    serializeError: (value: any) => {
      return {
        message: value?.message || 'Unknown error occurred',
        name: value?.name || 'Error',
        stack: value?.stack,
      };
    },
  },
);

// Fetch past events thunk (for loading previous events)
export const fetchPastEvents = createAppAsyncThunk(
  'events/fetchPastEvents',
  async (_, {getState}) => {
    try {
      const LIMIT_COUNT = 10;
      const {lastVisiblePast} = EventSelector.getEvents(getState());
      const userCompanyId = await getAuthUserCompanyId();

      const {events, lastVisibleSnapshot} = await Api.event.getPastEvents(
        userCompanyId,
        LIMIT_COUNT,
        lastVisiblePast,
      );

      // Filter events for employees (admins see all events)
      const state = getState();
      const user = UserSelector.getUserDetails(state);
      const company = CompanySelector.getCompanyDetails(state);
      const filteredEvents = user
        ? filterEventsForUser(events, user, company)
        : events;

      // Return payload with filtered events and lastVisible document snapshot
      return {
        events: filteredEvents,
        lastVisiblePast: lastVisibleSnapshot,
        isNextPage: !!lastVisiblePast,
      };
    } catch (error) {
      console.error('Error fetching past events:', error);
      throw new Error('Error fetching past events');
    }
  },
  {
    serializeError: (value: any) => {
      return {
        message: value?.message || 'Unknown error occurred',
        name: value?.name || 'Error',
        stack: value?.stack,
      };
    },
  },
);

// Keep the old fetchEvents for backward compatibility
export const fetchEvents = createAppAsyncThunk(
  'events/fetchEvents',
  async (_, {getState}) => {
    try {
      const LIMIT_COUNT = 10;
      const {lastVisible} = EventSelector.getEvents(getState());
      const userCompanyId = await getAuthUserCompanyId();

      const {events, lastVisibleSnapshot} = await Api.event.getEvents(
        userCompanyId,
        LIMIT_COUNT,
        lastVisible,
      );

      // Return payload with events and lastVisible document snapshot
      return {
        events,
        lastVisible: lastVisibleSnapshot,
        isNextPage: !!lastVisible,
      };
    } catch (error) {
      console.error('Error fetching events:', error);
      throw new Error('Error fetching events');
    }
  },
  {
    serializeError: (value: any) => {
      return {
        message: value?.message || 'Unknown error occurred',
        name: value?.name || 'Error',
        stack: value?.stack,
      };
    },
  },
);

// Update event details thunk
export const updateEventDetails = createAppAsyncThunk(
  'events/updateEventDetails',
  async (
    params: {
      eventId: string;
      updates: UpdateEventDetailsProps;
    },
    {getState},
  ) => {
    try {
      const userCompanyId = await getAuthUserCompanyId();

      await Api.event.updateEventDetails(
        userCompanyId,
        params.eventId,
        params.updates,
      );

      // Fetch the updated event to return the latest data
      const updatedEvent = await Api.event.getEventById(
        userCompanyId,
        params.eventId,
      );

      if (!updatedEvent) {
        throw new Error('Event not found after update');
      }

      return updatedEvent;
    } catch (error) {
      console.error('Error updating event details:', error);
      throw new Error('Error updating event details');
    }
  },
  {
    serializeError: (value: any) => {
      return {
        message: value?.message || 'Unknown error occurred',
        name: value?.name || 'Error',
        stack: value?.stack,
      };
    },
  },
);

// Fetch drafts thunk
export const fetchDrafts = createAppAsyncThunk(
  'events/fetchDrafts',
  async () => {
    try {
      const userCompanyId = await getAuthUserCompanyId();
      const drafts = await Api.draft.getDrafts(userCompanyId);
      
      // Sort drafts by last modified date (most recent first)
      return drafts.sort((a, b) => {
        const dateA = a.lastModified.toDate();
        const dateB = b.lastModified.toDate();
        return dateB.getTime() - dateA.getTime();
      });
    } catch (error) {
      console.error('Error fetching drafts:', error);
      throw new Error('Error fetching drafts');
    }
  },
  {
    serializeError: (value: any) => {
      return {
        message: value?.message || 'Unknown error occurred',
        name: value?.name || 'Error',
        stack: value?.stack,
      };
    },
  },
);

// Delete draft thunk
export const deleteDraftThunk = createAppAsyncThunk(
  'events/deleteDraft',
  async (draftId: string) => {
    try {
      const userCompanyId = await getAuthUserCompanyId();
      await Api.draft.deleteDraft(userCompanyId, draftId);
      return draftId;
    } catch (error) {
      console.error('Error deleting draft:', error);
      throw new Error('Error deleting draft');
    }
  },
  {
    serializeError: (value: any) => {
      return {
        message: value?.message || 'Unknown error occurred',
        name: value?.name || 'Error',
        stack: value?.stack,
      };
    },
  },
);
